import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import {
    Eye,
    UserPlus,
    MoreVertical,
    AlertCircle,
    User2,
    Clock,
    CheckCircle,
} from 'lucide-react';
import { Button } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import SubmissionDetails from '../../author/components/SubmissionDetails';
import Pagination from '../components/Pagination';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    submissionService,
    categoryAdminService,
    cycleService,
    SubmissionWithReviewer,
    Reviewer,
    Category,
    Cycle,
    GeoPoliticalZone
} from '../services/adminAuth';
import { Modal, FormGroup, FormLabel, FormSelect, MultiSelect } from '../components/adminModals';
import { useAuth } from '../../../auth/hooks/auth';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDateForInput, parseDateFromInput } from '../../../utils/dateHelper';

function ReviewerAssignment() {
    const [submissions, setSubmissions] = useState<SubmissionWithReviewer[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<SubmissionWithReviewer | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
    const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isTagChairman = user?.roleId === 5;

    const isSubmissionReassignmentEnabled = (sub: SubmissionWithReviewer | undefined | null) =>
        !!sub?.reviewers?.some(r => r.enableReassignment === true);

    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [isAutoModalOpen, setIsAutoModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [autoForm, setAutoForm] = useState({
        cycleId: 0,
        applicationType: 0,
        categoryId: 0,
        reviewersPerSubmission: 1
    });

    const [manualReviewers, setManualReviewers] = useState<{ value: string | number; label: string }[]>([]);
    const [selectedReviewerIds, setSelectedReviewerIds] = useState<(string | number)[]>([]);
    const [fetchingReviewers, setFetchingReviewers] = useState(false);
    const [manualDeadline, setManualDeadline] = useState<string>('');

    // Reassignment modal state
    const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
    const [selectedOldReviewers, setSelectedOldReviewers] = useState<string[]>([]);
    const [reassignmentPairs, setReassignmentPairs] = useState<{ oldId: string; newId: string }[]>([]);
    const [reassignReason, setReassignReason] = useState('');

    const [categories, setCategories] = useState<Category[]>([]);
    const [cycles, setCycles] = useState<Cycle[]>([]);
    const [zones, setZones] = useState<GeoPoliticalZone[]>([]);
    // For category admins: null = not yet resolved, 0 = none assigned
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);

    // Track whether async init has completed before reactive fetching begins
    const [initialized, setInitialized] = useState(false);
    const [activeCycle, setActiveCycle] = useState<Cycle | null>(null);
    const [hasInitialFetch, setHasInitialFetch] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [filters, setFilters] = useState({
        search: '',
        categoryId: 0,
        cycleId: 0,
        submittedFrom: null as string | null,
        submittedTo: null as string | null,
        applicationType: 0,
        geoPoliticalZoneId: 0
    });


    useEffect(() => {
        const init = async () => {
            await fetchDropdownData();
            setInitialized(true);
        };
        init();
    }, []);


    useEffect(() => {
        if (!initialized) return;

        // Skip initial fetch if activeCycle exists and hasn't been fetched yet
        if (activeCycle && !hasInitialFetch) return;

        // For category admins, block fetch until their category has been resolved
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;

        // For category admins with no assigned category, never fetch
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 500);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, filters, categoryAdminCategoryId, activeCycle, hasInitialFetch]);


    const fetchDropdownData = async () => {
        try {
            const [categoriesRes, cyclesRes, activeCycleRes, zonesRes] = await Promise.all([
                categoryAdminService.getCategoriesForCategoryAdmin(),
                cycleService.getAllCycles(),
                cycleService.getRunningCycle(),
                submissionService.getGeoPoliticalZones()
            ]);

            if (categoriesRes.isSuccess && categoriesRes.data) {
                const fetchedCategories = categoriesRes.data;
                setCategories(fetchedCategories);

                if (isCategoryAdmin) {
                    if (fetchedCategories.length > 0) {
                        const assignedId = fetchedCategories[0].id;
                        setCategoryAdminCategoryId(assignedId);
                        setFilters(prev => ({ ...prev, categoryId: assignedId }));
                        setAutoForm(prev => ({ ...prev, categoryId: assignedId }));
                    } else {
                        setCategoryAdminCategoryId(0);
                        setLoading(false);
                    }
                }
            }


            if (activeCycleRes.isSuccess && activeCycleRes.data) {
                const activeCycle = activeCycleRes.data;
                setActiveCycle(activeCycle);
                setCycles([activeCycle]);
                setFilters(prev => ({ ...prev, cycleId: activeCycle.id }));
                setAutoForm(prev => ({ ...prev, cycleId: activeCycle.id }));
                // Fetch submissions immediately with running cycle
                fetchSubmissions(activeCycle.id);
                setHasInitialFetch(true);
            } else if (cyclesRes.isSuccess && cyclesRes.data) {
                // Fallback to active cycles or all cycles
                setActiveCycle(null);
                setCycles(cyclesRes.data);
                setHasInitialFetch(true);
                if (cyclesRes.data!.length > 0) {
                    setAutoForm(prev => ({ ...prev, cycleId: cyclesRes.data![0].id }));
                }
            }

            if (zonesRes.isSuccess && zonesRes.data) setZones(zonesRes.data);
        } catch (err) {
            if (isCategoryAdmin) {
                setCategoryAdminCategoryId(0);
                setLoading(false);
            }
        }
    };

    const fetchSubmissions = async (cycleIdOverride?: number) => {
        const cycleId = cycleIdOverride !== undefined ? cycleIdOverride : Number(filters.cycleId);

        if (!cycleId || cycleId === 0) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                pageNumber: currentPage,
                pageSize: pageSize,
                sortBy: "",
                sortDescending: true,
                search: filters.search || "",
                categoryId: Number(filters.categoryId) || 0,
                cycleId: cycleId,
                authorId: "00000000-0000-0000-0000-000000000000",
                submittedFrom: filters.submittedFrom || null,
                submittedTo: filters.submittedTo || null,
                applicationType: 0, // Pass 0 as requested
                geoPoliticalZoneId: 0 // Pass 0 as requested
            };

            const response = await submissionService.getSubmissionsWithReviewers(payload);

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Fetch Failed',
                    message: response.message || 'Failed to fetch submissions'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while fetching submissions'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, search: query }));
        setCurrentPage(1);
    };

    const handleFilterChange = (field: string, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : 0,
            cycleId: activeCycle ? activeCycle.id : 0,
            submittedFrom: null,
            submittedTo: null,
            applicationType: 0,
            geoPoliticalZoneId: 0
        });
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleOpenManualModal = async (submission: SubmissionWithReviewer) => {
        setSelectedSubmission(submission);
        setIsManualModalOpen(true);
        setActionMenuOpen(null);
        setFetchingReviewers(true);
        setSelectedReviewerIds([]);

        try {
            const response = await submissionService.getReviewersByCategory(submission.categoryId);
            if (response.isSuccess && response.data) {
                const formatted = (response.data as any[]).map(r => ({
                    value: r.id,
                    label: r.name
                }));
                setManualReviewers(formatted);
            } else {
                showNotification('error', {
                    title: 'Fetch Failed',
                    message: response.message || 'Failed to fetch reviewers for this thematic area'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while fetching reviewers'
            });
        } finally {
            setFetchingReviewers(false);
        }
    };

    const handleManualAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSubmission || selectedReviewerIds.length !== 1 || !manualDeadline) return;
        setIsSubmitting(true);
        try {
            const response = await submissionService.manualAssignment({
                submissionId: selectedSubmission.submissionId,
                reviewerIds: selectedReviewerIds.map(id => id.toString()),
                ignoreCategory: true,
                deadLineDate: parseDateFromInput(manualDeadline)
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Assigned successfully' });
                setIsManualModalOpen(false);
                setManualDeadline('');
                setSelectedReviewerIds([]);
                fetchSubmissions();
            } else {
                showNotification('error', {
                    title: 'Assignment Failed',
                    message: response.message || 'Failed to assign reviewers'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred during manual assignment'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAutoAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await submissionService.automaticAssignment(autoForm);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Auto-assignment started' });
                setIsAutoModalOpen(false);
                fetchSubmissions();
            } else {
                showNotification('error', {
                    title: 'Auto-Assignment Failed',
                    message: response.message || 'Failed to start automatic assignment'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred during automatic assignment'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAllowReAssignment = async (submission: SubmissionWithReviewer) => {
        setActionMenuOpen(null);
        try {
            const response = await submissionService.allowReAssignment(submission.submissionId);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: response.message || 'Submission enabled for reassignment' });
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Failed', message: response.message || 'Could not enable reassignment' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while enabling reassignment' });
        }
    };

    const handleOpenReassignModal = async (submission: SubmissionWithReviewer) => {
        setSelectedSubmission(submission);
        setIsReassignModalOpen(true);
        setActionMenuOpen(null);
        setSelectedOldReviewers([]);
        setReassignmentPairs([]);
        setReassignReason('');
        setFetchingReviewers(true);

        try {
            const response = await submissionService.getReviewersByCategory(submission.categoryId);
            if (response.isSuccess && response.data) {
                const formatted = (response.data as any[]).map(r => ({
                    value: r.id,
                    label: r.name
                }));
                setManualReviewers(formatted);
            } else {
                showNotification('error', {
                    title: 'Fetch Failed',
                    message: response.message || 'Failed to fetch reviewers for reassignment'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while fetching reviewers for reassignment'
            });
        } finally {
            setFetchingReviewers(false);
        }
    };

    const handleReassignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSubmission || reassignmentPairs.length === 0 || !reassignReason.trim()) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select reviewers to reassign and provide a reason' });
            return;
        }

        const pair = reassignmentPairs[0];
        if (!pair || !pair.newId) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select a replacement reviewer' });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await submissionService.reassignReviewer({
                submissionId: selectedSubmission.submissionId,
                oldReviewerId: pair.oldId,
                newReviewerId: pair.newId,
                reason: reassignReason
            });

            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Reviewers reassigned successfully' });
                setIsReassignModalOpen(false);
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to reassign reviewers' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while reassigning reviewers' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getReviewerDisplay = (reviewers: Reviewer[]) => {
        if (!reviewers || reviewers.length === 0) {
            return (
                <div className="flex items-center gap-2 text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium italic">Unassigned</span>
                </div>
            );
        }
        return (
            <div className="flex flex-col gap-2.5">
                {reviewers.map(r => (
                    <div key={r.reviewerId} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2 text-gray-700">
                            <div className="bg-gray-100 p-1 rounded-full">
                                <User2 className="w-3.5 h-3.5 text-gray-600" />
                            </div>
                            <span className="text-sm font-medium">{r.reviewerName}</span>
                        </div>
                        <div className="ml-7">
                            {r.isCompleted ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700">
                                    <CheckCircle className="w-3 h-3" />Completed
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                                    <Clock className="w-3 h-3" />Pending
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const getStatusBadge = (status: string) => {
        const statusMap: { [key: string]: string } = {
            'Submitted': 'bg-gray-100 text-gray-700',
            'Pending': 'bg-gray-100 text-gray-700',
            'Under Review': 'bg-blue-50 text-blue-700',
            'Passed': 'bg-green-50 text-green-700',
            'Failed': 'bg-red-50 text-red-700'
        };
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${statusMap[status] || 'bg-gray-100 text-gray-700'}`}>
                {status}
            </span>
        );
    };

    const renderCategoryDropdown = () => {
        if (isCategoryAdmin) {
            if (categoryAdminCategoryId === 0 || categories.length === 0) {
                return (
                    <select
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
                    >
                        <option>No Thematic Area</option>
                    </select>
                );
            }
            return (
                <select
                    disabled
                    value={categories[0].id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                    onChange={() => undefined}
                >
                    <option value={categories[0].id}>{categories[0].name}</option>
                </select>
            );
        }

        return (
            <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-md text-sm"
            >
                <option value={0}>Select Thematic Area</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
        );
    };


    const emptyMessage = isCategoryAdmin && categoryAdminCategoryId === 0
        ? 'No Thematic Area Assigned to this Admin yet'
        : 'No submissions found.';


    const columns: TableColumn<SubmissionWithReviewer>[] = [
        { key: 'submissionNumber', label: 'ID', render: (val) => <span className="text-gray-500 text-sm font-mono">{val}</span> },
        {
            key: 'title',
            label: 'Title / Author',
            render: (_, row) => {
                const authorsStr = row.authors && String(row.authors).trim();
                if (authorsStr) {
                    return (
                        <div>
                            <div className="font-medium text-gray-900 mb-1">{row.title}</div>
                            <div className="text-sm text-gray-500">{authorsStr}</div>
                        </div>
                    );
                }
                const bookAuthors = (row as any).bookAuthors as Array<any> | undefined;
                  const bookAuthorsStr = bookAuthors && bookAuthors.length > 0
                      ? bookAuthors.map(a => a?.name ?? '').filter(Boolean).join(', ')
                      : '';
                  const submitter = (row as any).fullName || row.fullName || '—';
                return (
                    <div>
                        <div className="font-medium text-gray-900 mb-1">{row.title}</div>
                          <div className="text-sm text-gray-500">{bookAuthorsStr || submitter}</div>
                    </div>
                );
            }
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            render: (val) => (
                <span className="text-sm text-gray-600 whitespace-nowrap">
                    {val ? new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }) : '—'}
                </span>
            ),
        },
        // { key: 'geoPoliticalZone', label: 'Zone' },
        { key: 'categoryName', label: 'Thematic Area' },
        { key: 'reviewers', label: 'Reviewers', render: (val) => getReviewerDisplay(val) },
        { key: 'status', label: 'Submission Status', render: (val) => getStatusBadge(val) }
    ];

    const actions: TableAction<SubmissionWithReviewer>[] = [
        // {
        //     icon: <Eye className="w-5 h-5" />,
        //     onClick: (row) => {
        //         setSelectedSubmission(row);
        //         setIsDetailsOpen(true);
        //     },
        //     title: "View Details",
        //     variant: 'icon',
        //     className: "text-blue-500 hover:text-blue-700"
        // },
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: (row, e) => {
                if (e) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const menuHeight = 100;
                    const viewportHeight = window.innerHeight;

                    let top = rect.bottom + 4;
                    if (rect.bottom + menuHeight > viewportHeight) {
                        top = rect.top - menuHeight - 4;
                    }

                    setActionMenuPosition({
                        top: top,
                        left: rect.left - 220
                    });
                    setActionMenuOpen(row.submissionId);
                }
            },
            variant: 'icon'
        }
    ];

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    {renderCategoryDropdown()}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <select
                        value={filters.cycleId}
                        onChange={(e) => handleFilterChange('cycleId', parseInt(e.target.value))}
                        disabled={!!activeCycle}
                        className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        {activeCycle ? (
                            <option value={activeCycle.id}>{activeCycle.name}</option>
                        ) : (
                            <>
                                <option value={0}>Select Cycle</option>
                                {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </>
                        )}
                    </select>
                </div>
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select value={filters.applicationType} onChange={(e) => handleFilterChange('applicationType', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-md text-sm">
                        <option value={0}>Select Type</option>
                        <option value={1}>Book</option>
                        <option value={2}>Journal Article</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
                    <select value={filters.geoPoliticalZoneId} onChange={(e) => handleFilterChange('geoPoliticalZoneId', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-md text-sm">
                        <option value={0}>Select Zone</option>
                        {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                    </select>
                </div> */}
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">Clear all filters</button>
        </div>
    );

    return (
        <div className="w-full">
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Reviewers Assignment
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Manage manuscript assignments
                    </p>
                </div>

                {/* <div className="flex w-full lg:w-auto">
                    <Button
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={() => setIsAutoModalOpen(true)}
                    >
                        Automatic Assignment
                    </Button>
                </div> */}
            </div>

            <ReusableTable
                columns={columns}
                data={submissions}
                loading={loading}
                onSearch={handleSearch}
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
                actions={actions}
                emptyMessage={emptyMessage}
            />

            {actionMenuOpen && actionMenuPosition && (() => {
                const sub = submissions.find(s => s.submissionId === actionMenuOpen);
                const hasReviewers = !!sub?.reviewers && sub.reviewers.length > 0;
                const enabled = isSubmissionReassignmentEnabled(sub);
                return (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setActionMenuOpen(null)} />
                    <div className="fixed bg-white shadow-xl border rounded-lg py-2 z-50 w-56" style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}>
                        <button className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2" onClick={() => {
                            if (sub) { setSelectedSubmission(sub); setIsDetailsOpen(true); }
                            setActionMenuOpen(null);
                        }}><Eye size={16} /> View Details</button>

                        {isTagChairman ? (
                            !hasReviewers ? (
                                <button className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2" onClick={() => { if (sub) handleOpenManualModal(sub); }}>
                                    <UserPlus size={16} /> Assign Reviewer
                                </button>
                            ) : enabled ? (
                                <button className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2" onClick={() => { if (sub) handleOpenReassignModal(sub); }}>
                                    <UserPlus size={16} /> Reassign Reviewer
                                </button>
                            ) : (
                                <button className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2" onClick={() => { if (sub) handleAllowReAssignment(sub); }}>
                                    <UserPlus size={16} /> Enable for Reassignment
                                </button>
                            )
                        ) : (
                            <button className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2" onClick={() => {
                                if (sub) {
                                    if (hasReviewers) {
                                        handleOpenReassignModal(sub);
                                    } else {
                                        handleOpenManualModal(sub);
                                    }
                                }
                            }}>
                                <UserPlus size={16} /> {hasReviewers ? 'Reassign Reviewer' : 'Assign Reviewer'}
                            </button>
                        )}
                    </div>
                </>
                );
            })()}

            <Pagination currentPage={currentPage} totalPages={totalPages} totalRecords={totalRecords} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />

            <Drawer isOpen={isDetailsOpen} onClose={() => setIsDetailsOpen(false)} title="Submission Details" width="max-w-3xl">
                {selectedSubmission && (
                    <SubmissionDetails submissionId={selectedSubmission.submissionId} />
                )}
            </Drawer>

            <Modal isOpen={isManualModalOpen} onClose={() => { setIsManualModalOpen(false); setManualDeadline(''); setSelectedReviewerIds([]); }} title="Manual Assignment">
                <form onSubmit={handleManualAssignSubmit}>
                    <FormGroup>
                        <FormLabel required>Select exactly 1 reviewer</FormLabel>
                        <MultiSelect
                            options={manualReviewers}
                            selectedValues={selectedReviewerIds}
                            onChange={(vals) => setSelectedReviewerIds(vals as (string | number)[])}
                            placeholder={fetchingReviewers ? "Loading..." : "Select Reviewer"}
                            maxSelection={1}
                        />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel required>Review Deadline</FormLabel>
                        <DatePicker
                            selected={manualDeadline ? new Date(manualDeadline) : null}
                            onChange={(date: Date | null) => setManualDeadline(date ? formatDateForInput(date.toISOString()) : '')}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy h:mm aa"
                            minDate={new Date()}
                            className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholderText="DD/MM/YYYY HH:MM"
                            wrapperClassName="w-full"
                        />
                    </FormGroup>
                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="white" onClick={() => { setIsManualModalOpen(false); setManualDeadline(''); setSelectedReviewerIds([]); }}>Cancel</Button>
                        <Button variant="success" type="submit" isLoading={isSubmitting} disabled={selectedReviewerIds.length !== 1 || !manualDeadline}>Assign</Button>
                    </div>
                </form>
            </Modal>

            {/* Auto Assignment Modal */}
            <Modal isOpen={isAutoModalOpen} onClose={() => setIsAutoModalOpen(false)} title="Automatic Assignment">
                <form onSubmit={handleAutoAssignSubmit}>
                    <div className="grid grid-cols-2 gap-4">
                        <FormGroup>
                            <FormLabel>Cycle</FormLabel>
                            <FormSelect
                                value={autoForm.cycleId.toString()}
                                onChange={(e) => setAutoForm({ ...autoForm, cycleId: parseInt(e.target.value) })}
                            >
                                <option value="0">Select Cycle</option>
                                {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </FormSelect>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>Thematic Area</FormLabel>
                            {isCategoryAdmin ? (
                                // Category admin: locked to their assigned category
                                categoryAdminCategoryId === 0 || categories.length === 0 ? (
                                    <FormSelect value="0" onChange={() => undefined} disabled>
                                        <option value="0">No Thematic Area Assigned</option>
                                    </FormSelect>
                                ) : (
                                    <FormSelect value={categories[0].id.toString()} onChange={() => undefined} disabled>
                                        <option value={categories[0].id}>{categories[0].name}</option>
                                    </FormSelect>
                                )
                            ) : (
                                <FormSelect
                                    value={autoForm.categoryId.toString()}
                                    onChange={(e) => setAutoForm({ ...autoForm, categoryId: parseInt(e.target.value) })}
                                >
                                    <option value="0">Select Thematic Area</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </FormSelect>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>Application Type</FormLabel>
                            <FormSelect
                                value={autoForm.applicationType.toString()}
                                onChange={(e) => setAutoForm({ ...autoForm, applicationType: parseInt(e.target.value) })}
                            >
                                <option value={0}>Select Application Type</option>
                                <option value={1}>Book</option>
                                <option value={2}>Journal Article</option>
                            </FormSelect>
                        </FormGroup>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="white" onClick={() => setIsAutoModalOpen(false)}>Cancel</Button>
                        <Button
                            variant="success"
                            type="submit"
                            isLoading={isSubmitting}
                            disabled={
                                autoForm.cycleId === 0 ||
                                autoForm.applicationType === 0 ||
                                (isCategoryAdmin && (categoryAdminCategoryId === 0 || categories.length === 0))
                            }
                        >
                            Start Assignment
                        </Button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={isReassignModalOpen} onClose={() => setIsReassignModalOpen(false)} title="Reviewer Reassignment">
                <form onSubmit={handleReassignSubmit}>
                    <FormGroup>
                        <FormLabel required>Select Reviewer to Replace</FormLabel>
                        <div className="space-y-2 border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto">
                            {selectedSubmission?.reviewers && selectedSubmission.reviewers.length > 0 ? (
                                selectedSubmission.reviewers.map((reviewer) => (
                                    <label key={reviewer.reviewerId} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                        <input
                                            type="checkbox"
                                            checked={reassignmentPairs.some(p => p.oldId === reviewer.reviewerId)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    if (reassignmentPairs.length < 1) {
                                                        setReassignmentPairs([...reassignmentPairs, { oldId: reviewer.reviewerId, newId: '' }]);
                                                    }
                                                } else {
                                                    setReassignmentPairs(reassignmentPairs.filter(p => p.oldId !== reviewer.reviewerId));
                                                }
                                            }}
                                            disabled={!reassignmentPairs.some(p => p.oldId === reviewer.reviewerId) && reassignmentPairs.length >= 1}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{reviewer.reviewerName}</span>
                                    </label>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500 italic">No reviewers assigned</p>
                            )}
                        </div>
                    </FormGroup>

                    {reassignmentPairs.length > 0 && (
                        <div className="space-y-3 mt-4">
                            <FormLabel required>Select Replacement Reviewers</FormLabel>
                            {reassignmentPairs.map((pair, idx) => {
                                const oldReviewer = selectedSubmission?.reviewers?.find(r => r.reviewerId === pair.oldId);
                                const assignedReviewerIds = selectedSubmission?.reviewers?.map(r => r.reviewerId) || [];
                                const availableReviewers = manualReviewers.filter(r => !assignedReviewerIds.includes(r.value as string));

                                return (
                                    <div key={pair.oldId} className="bg-gray-50 p-3 rounded-md">
                                        <p className="text-xs text-gray-500 mb-2">Replace: <span className="font-semibold text-gray-700">{oldReviewer?.reviewerName}</span></p>
                                        <FormSelect
                                            value={pair.newId}
                                            onChange={(e) => {
                                                const updated = [...reassignmentPairs];
                                                updated[idx].newId = e.target.value;
                                                setReassignmentPairs(updated);
                                            }}
                                        >
                                            <option value="">Select Replacement</option>
                                            {availableReviewers.map(r => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                        </FormSelect>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <FormGroup>
                        <FormLabel required>Reason for Reassignment</FormLabel>
                        <textarea
                            value={reassignReason}
                            onChange={(e) => setReassignReason(e.target.value)}
                            placeholder="Provide a reason for reassigning the reviewer(s)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            rows={3}
                            required
                        />
                    </FormGroup>

                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="white" onClick={() => setIsReassignModalOpen(false)}>Cancel</Button>
                        <Button variant="success" type="submit" onClick={() => undefined} disabled={isSubmitting || reassignmentPairs.length === 0 || !reassignReason.trim()}>
                            Reassign
                        </Button>
                    </div>
                </form>
            </Modal>
        </div >
    );
}

export default ReviewerAssignment;